import React from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { PosilovaNotifikaceLogResponse } from '../api/JsdhNotifikaceService/generated/models/PosilovaNotifikaceLog';
import { formatCasHHmm } from '../utils/casUtils';
import { FitHeightBox } from './FitHeightBox';

interface ZpravyOdAdminaProps {
    notifikace: PosilovaNotifikaceLogResponse[];
    /**
     * Texty povolané posilové techniky z události (kumulativní, všechny cesty povolání).
     * Vozy, které nejsou v žádném záznamu POSILOVA_TECHNIKA v logu (KOPIS e-mail, povolení
     * bez rozeslané notifikace), se do slotu POSILA doplní z nich.
     */
    posilovaTechnikaTexty?: string[];
    sx?: SxProps<Theme>;
}

const formatTime = formatCasHHmm;

const vsechnyNejnovejsiPrvni = (
    list: PosilovaNotifikaceLogResponse[],
    typ: 'ZPRAVA' | 'POSILOVA_TECHNIKA'
): PosilovaNotifikaceLogResponse[] =>
    list
        .filter(n => (n.typ ?? 'ZPRAVA') === typ) // fallback: bez typ → ZPRAVA
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

const pickLatest = (
    list: PosilovaNotifikaceLogResponse[],
    typ: 'ZPRAVA' | 'POSILOVA_TECHNIKA'
): PosilovaNotifikaceLogResponse | null => vsechnyNejnovejsiPrvni(list, typ)[0] ?? null;

/**
 * True právě tehdy, když ZpravyOdAdmina něco vykreslí: poslední ZPRAVA, záznam
 * POSILOVA_TECHNIKA, nebo neprázdný text posilové techniky z události. Kiosek na výšku
 * podle toho rozhoduje, jestli sekce „Posila a zprávy" vůbec existuje.
 */
export const maZpravy = (notifikace: PosilovaNotifikaceLogResponse[], posilovaTechnikaTexty?: string[]): boolean =>
    !!pickLatest(notifikace, 'ZPRAVA')
    || !!pickLatest(notifikace, 'POSILOVA_TECHNIKA')
    || (posilovaTechnikaTexty ?? []).some(t => !!t && !!t.trim());

const radekSx = { fontSize: '1em', fontWeight: 500, lineHeight: 1.3, overflowWrap: 'break-word' } as const;

const PREFIX_POVOLANA = 'Povolána technika: ';

const bezPrefixu = (text: string) =>
    text.startsWith(PREFIX_POVOLANA) ? text.slice(PREFIX_POVOLANA.length) : text;

// Všechny záznamy o povolané posile v jednom řádku, nejnovější první. Autor a čas nejnovějšího
// jsou v hlavičce řádku, u starších je čas v závorce za textem; opakovaný prefix
// „Povolána technika:" se píše jen jednou.
const textPosil = (posily: PosilovaNotifikaceLogResponse[]): string => {
    const [nejnovejsi, ...starsi] = posily;
    if (starsi.length === 0) return nejnovejsi.text ?? '';
    const vsechnyPovolani = posily.every(p => (p.text ?? '').startsWith(PREFIX_POVOLANA));
    const dalsi = starsi.map(p => `${vsechnyPovolani ? bezPrefixu(p.text ?? '') : (p.text ?? '')} (${formatTime(p.createdAt)})`);
    return [nejnovejsi.text ?? '', ...dalsi].join('; ');
};

// Zprávy dostanou jen místo, které v panelu zbude (rodič je flex sloupec se stropem výšky).
// Řádek POSILA má přednost: zpráva admina se při nedostatku místa smrští jako první
// (flex-shrink 100 proti 1), zmenší písmo nejvýš na 16 px a teprve pak se zkrátí na celé
// řádky s „…". POSILA se tak vždy vejde celá, dokud je panel aspoň tak vysoký jako ona.
export const ZpravyOdAdmina: React.FC<ZpravyOdAdminaProps> = ({ notifikace, posilovaTechnikaTexty, sx }) => {
    const obecna = pickLatest(notifikace, 'ZPRAVA');
    const posilyLog = vsechnyNejnovejsiPrvni(notifikace, 'POSILOVA_TECHNIKA');
    const posilaLog = posilyLog[0] ?? null;
    const posilaTexty = (posilovaTechnikaTexty ?? []).filter(t => t && t.trim());
    const posilaZUdalosti = !posilaLog && posilaTexty.length > 0;

    if (!obecna && !posilaLog && !posilaZUdalosti) return null;

    // posilovaTechnikaTexty jsou kumulativní (KOPIS e-mail, povolení s notifikací i bez ní), log
    // má jen povolení s rozeslanou notifikací. Vozy, které v žádném záznamu logu nejsou, se proto
    // doplní za texty z logu, jinak by posila z KOPIS e-mailu po první notifikaci zmizela.
    const mimoLog = posilaTexty.filter(t => !posilyLog.some(p => (p.text ?? '').includes(t)));
    const posilaText = posilaLog
        ? [textPosil(posilyLog), ...(mimoLog.length > 0 ? [mimoLog.join(', ')] : [])].join('; ')
        : posilaTexty.join(', ');

    return (
        <Box data-testid="zpravy-od-admina"
            sx={{ p: 1, borderRadius: 1, bgcolor: 'background.default', fontSize: '18px',
                display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, ...(sx as object) }}>
            <Typography sx={{ flex: '0 0 auto', fontSize: '1rem', fontWeight: 700, color: 'text.secondary', mb: 0.5, lineHeight: 1.2 }}>
                Zprávy:
            </Typography>
            {obecna && (
                <FitHeightBox data-testid="zprava-obecna-box" maxFontPx={18} orezRadky fitKey={`${obecna.id}|${obecna.text}`}
                    sx={{ flex: '0 100 auto', mb: (posilaLog || posilaZUdalosti) ? 0.75 : 0 }}>
                    <Typography data-testid="zprava-obecna" sx={radekSx}>
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                            {obecna.odeslalJmeno || 'Admin'} ({formatTime(obecna.createdAt)}):
                        </Box>
                        {' '}
                        {obecna.text}
                    </Typography>
                </FitHeightBox>
            )}
            {(posilaLog || posilaZUdalosti) && (
                <FitHeightBox data-testid="zprava-posila-box" maxFontPx={18} orezRadky fitKey={posilaText}
                    sx={{ flex: '0 1 auto' }}>
                    <Typography data-testid="zprava-posila" sx={radekSx}>
                        <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
                            {posilaLog
                                ? `POSILA — ${posilaLog.odeslalJmeno || 'Admin'} (${formatTime(posilaLog.createdAt)}):`
                                : 'POSILA — povolána technika:'}
                        </Box>
                        {' '}
                        {posilaText}
                    </Typography>
                </FitHeightBox>
            )}
        </Box>
    );
};
